// Lib
import { isObject, capitalize } from 'lodash/fp';

// Utils
import { getStartOfDayDate, isValidDate, TIMES } from '../timeUtil';
import { DEFAULT_LOCALE, UI_TO_CMS_MAP } from '../../cms/cmsLocaleConstants';
import {
    getFriendlyDateFormatOptions,
    FriendlyDateFormatStyle,
    FRIENDLY_DATE_FORMAT_STYLES,
} from './intlDateFormatPresets';

const normalizeLocaleTag = (localeTag: string): string | null => {
    const trimmedLocaleTag = localeTag.trim();
    const [languageCode, regionCode] = trimmedLocaleTag.split('-');
    if (!languageCode || !regionCode) return null;

    return `${languageCode.toLowerCase()}-${regionCode.toUpperCase()}`;
};

const getCanonicalIntlLocale = (localeTag: string | null): string | null => {
    if (!localeTag) return null;

    try {
        const [canonicalLocale] = Intl.getCanonicalLocales(localeTag);
        return canonicalLocale || null;
    } catch {
        return null;
    }
};

const getDefaultIntlLocale = (): string => {
    const defaultIntlLocale = getCanonicalIntlLocale(normalizeLocaleTag(DEFAULT_LOCALE));

    if (!defaultIntlLocale) {
        throw new Error(`Invalid default app locale configuration: "${DEFAULT_LOCALE}"`);
    }

    return defaultIntlLocale;
};

const DEFAULT_INTL_LOCALE = getDefaultIntlLocale();

/**
 * Formats relative day labels using Intl so grammar and language are locale-native.
 * `offsetDays` is expected to be one of: -1 (yesterday), 0 (today), 1 (tomorrow).
 */
const formatRelativeDay = (offsetDays: number, locale: string): string => {
    const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return capitalize(relativeTimeFormatter.format(offsetDays, 'day'));
};

/**
 * Formats non-relative dates using shared Intl preset options.
 * The preset branch (`withinYear` vs `crossYear`) is chosen against `relativeTimestamp`.
 */
const formatCalendarDate = (
    targetTimestamp: number,
    referenceTimestamp: number,
    locale: string,
    style: FriendlyDateFormatStyle,
): string => {
    const targetDate = new Date(targetTimestamp);
    const relativeDate = new Date(referenceTimestamp);
    const isWithinCurrentYear = targetDate.getFullYear() === relativeDate.getFullYear();
    const formatOptions = getFriendlyDateFormatOptions({
        style,
        isWithinCurrentYear,
    });

    return new Intl.DateTimeFormat(locale, formatOptions).format(targetDate);
};

/**
 * Maps app locale preferences to a normalized Intl locale.
 *
 * Supported inputs:
 * - CMS-style locale preference (e.g. `en-au`, `es-es`) -> `en-AU`, `es-ES`
 * - Language-only code (e.g. `en`, `ja`) -> looked up via `UI_TO_CMS_MAP`
 *
 * Fallback:
 * - Unknown or missing locale -> app default locale from settings constants
 */
export const mapLocalePreferenceToIntlLocale = (localePreference?: string): string => {
    if (!localePreference) return DEFAULT_INTL_LOCALE;

    const localeFromPreference = getCanonicalIntlLocale(normalizeLocaleTag(localePreference));
    if (localeFromPreference) return localeFromPreference;

    const normalizedLang = localePreference.trim().toLowerCase() as keyof typeof UI_TO_CMS_MAP;
    const cmsLocale = UI_TO_CMS_MAP[normalizedLang];
    if (!cmsLocale) return DEFAULT_INTL_LOCALE;

    const localeFromLanguage = getCanonicalIntlLocale(normalizeLocaleTag(cmsLocale));
    return localeFromLanguage || DEFAULT_INTL_LOCALE;
};

/**
 * Formats past-or-current timestamps.
 *
 * Rules:
 * - Same local day as `relativeTimestamp` => Today
 * - Previous local day => Yesterday
 * - Everything else => preset-based calendar date
 */
const formatPastDateIntl = (
    targetTimestamp: number,
    referenceTimestamp: number,
    locale: string,
    style: FriendlyDateFormatStyle,
): string => {
    const startOfDayMillis = getStartOfDayDate(referenceTimestamp).getTime();

    // Same day: "Today"
    if (targetTimestamp >= startOfDayMillis) {
        return formatRelativeDay(0, locale);
    }

    const startOfYesterdayMillis = startOfDayMillis - TIMES.DAY;
    // Previous day: "Yesterday"
    if (targetTimestamp >= startOfYesterdayMillis) {
        return formatRelativeDay(-1, locale);
    }

    // Older than yesterday: locale-native calendar date.
    return formatCalendarDate(targetTimestamp, referenceTimestamp, locale, style);
};

/**
 * Formats future timestamps.
 *
 * Rules:
 * - Before tomorrow's start => Today
 * - Before day-after-tomorrow's start => Tomorrow
 * - Everything else => preset-based calendar date
 */
const formatFutureDateIntl = (
    targetTimestamp: number,
    referenceTimestamp: number,
    locale: string,
    style: FriendlyDateFormatStyle,
): string => {
    const startOfDayMillis = getStartOfDayDate(referenceTimestamp).getTime();
    const startOfTomorrowMillis = startOfDayMillis + TIMES.DAY;
    const startOfDayAfterTomorrowMillis = startOfTomorrowMillis + TIMES.DAY;

    // Later today
    if (targetTimestamp < startOfTomorrowMillis) {
        return formatRelativeDay(0, locale);
    }

    // Tomorrow
    if (targetTimestamp < startOfDayAfterTomorrowMillis) {
        return formatRelativeDay(1, locale);
    }

    // Beyond tomorrow: locale-native calendar date.
    return formatCalendarDate(targetTimestamp, referenceTimestamp, locale, style);
};

/**
 * Converts a timestamp into a friendly localized label.
 *
 * Output categories:
 * - Relative labels for near days: Today / Yesterday / Tomorrow
 * - Calendar dates for all other days, using shared Intl styles
 *
 * Notes:
 * - `timestamp` and `relativeTimestamp` accept `Date` or epoch millis.
 * - `style` controls date shape (`default` vs `compact`) when returning calendar dates.
 * - Locale fallback remains the app default locale.
 */
export const friendlyDateFormatterIntl = ({
    timestamp = Date.now(),
    relativeTimestamp = Date.now(),
    locale,
    style = FRIENDLY_DATE_FORMAT_STYLES.DEFAULT,
}: {
    timestamp?: number | Date;
    relativeTimestamp?: number | Date;
    locale?: string;
    style?: FriendlyDateFormatStyle;
} = {}) => {
    const targetTimestamp = isObject(timestamp) ? timestamp.getTime() : timestamp;
    const referenceTimestamp = isObject(relativeTimestamp) ? relativeTimestamp.getTime() : relativeTimestamp;

    if (!isValidDate(targetTimestamp) || !isValidDate(referenceTimestamp)) return '';

    const intlLocale = mapLocalePreferenceToIntlLocale(locale);

    return targetTimestamp > referenceTimestamp
        ? formatFutureDateIntl(targetTimestamp, referenceTimestamp, intlLocale, style)
        : formatPastDateIntl(targetTimestamp, referenceTimestamp, intlLocale, style);
};
